import type { NeueAufgabeVorgabe } from "../../kern/dialog";
import type { Prioritaet, Sichtbarkeit } from "./logik";

/* Vorgaben für eine neue Aufgabe (BAUPLAN-AUFGABENLISTE.md, Abschnitt 5). Das Modal „Neue Aufgabe“ und die Zeile „+ Aufgabe hinzufügen“
   rechnen beide hier, damit eine Aufgabe aus der Zeile genauso angelegt wird wie aus dem Knopf derselben Seite. */

export type Wo = "sprint" | "backlog";

export interface VorgabeKontext {
  ich: string;
  /** aufgaben.alle_bearbeiten: Vorgabe „Nur Vorstand“, sonst „Alle Mitglieder“ */
  vorstand: boolean;
  /** sprint.planen: nur dann kommt eine neue Aufgabe in den Sprint (sonst setzt die Datenbank sie in den Backlog) */
  darfPlanen: boolean;
  laufend: { id: string } | null | undefined;
  /** Ressort eines Vorhabens, solange die Liste der Vorhaben noch lädt undefined */
  ressortVon: (vorhaben: string) => string | null | undefined;
}

/** Was eine Liste zusätzlich festlegt: die Person oder das Ressort ihrer Gruppe */
export interface ListenVorgabe extends NeueAufgabeVorgabe {
  /** Eine bestimmte Person statt „ich“ (Gruppe nach Person). Für „ohne Person“ gilt `person: "offen"`. */
  personId?: string | undefined;
  /** Ressort der Gruppe, null heißt „Ohne Ressort“. Ohne Angabe: das Ressort des Vorhabens. */
  ressort?: string | null | undefined;
}

export interface Startwerte {
  personen: string[];
  prioritaet: Prioritaet;
  ressort: string | null;
  vorhaben: string | null;
  wo: Wo;
  sichtbarkeit: Sichtbarkeit;
  /** Der laufende Sprint, wenn die Aufgabe dort landet, sonst null (Backlog) */
  sprint: string | null;
}

/** Grundsicht ohne Vorhaben: Vorstand „Nur Vorstand“, Mitglieder „Alle Mitglieder“ */
export const grundSicht = (vorstand: boolean): Sichtbarkeit => (vorstand ? "vorstand" : "mitglieder");

/** Landet eine Aufgabe mit diesem Ort im laufenden Sprint? Nur mit sprint.planen, nie als Unteraufgabe. */
export function sprintFuer(wo: Wo, k: Pick<VorgabeKontext, "darfPlanen" | "laufend">, unteraufgabe = false): string | null {
  return k.darfPlanen && !unteraufgabe && wo === "sprint" && k.laufend ? k.laufend.id : null;
}

/** Startwerte wie im Modal: ich verantwortlich (außer „offen“), Priorität Normal, Sprint (außer „backlog“), Vorhaben mit „Kapsel“ und
 *  dessen Ressort, sonst die Grundsicht. Die Liste legt danach Person oder Ressort ihrer Gruppe fest. */
export function startwerte(vorgabe: ListenVorgabe, k: VorgabeKontext): Startwerte {
  const vorhaben = vorgabe.vorhaben ?? null;
  const wo: Wo = vorgabe.wo === "backlog" ? "backlog" : "sprint";
  const personen = vorgabe.person === "offen" ? [] : [vorgabe.personId ?? k.ich];
  return {
    personen,
    prioritaet: "normal",
    ressort: vorgabe.ressort !== undefined ? vorgabe.ressort : vorhaben ? (k.ressortVon(vorhaben) ?? null) : null,
    vorhaben,
    wo,
    sichtbarkeit: vorhaben ? "vorhaben" : grundSicht(k.vorstand),
    sprint: sprintFuer(wo, k, !!vorgabe.eltern),
  };
}

/** Gruppe nach Person (Aufgaben, Sprint): Vorgabe für die Zeile „+ Aufgabe hinzufügen“ oder null, wenn eine neue Aufgabe dort nicht
 *  landen soll. Mitglieder legen für sich oder offen an (aufgaben_anlegen). Für eine andere Person nur der Vorstand, und nur, wenn sie
 *  die Aufgabe mit der Vorgabe „Nur Vorstand“ auch sieht. Leere id heißt „ohne Person“. */
export function personGruppe(id: string, k: { ich: string; vorstand: boolean; personIstVorstand: boolean }): ListenVorgabe | null {
  if (!id) return { person: "offen" };
  if (id === k.ich) return {};
  return k.vorstand && k.personIstVorstand ? { personId: id } : null;
}
